import React from "react";
import { formatNumber, formatDate, formatMoney } from "@/lib/utils";
import { numberToVietnameseWords } from "@/lib/numberToWords";
import { getCompanyConfig } from "@/config/company";
import ISOPrintHeader from "@/components/shared/print/ISOPrintHeader";
import { PrintDocumentTitle, PrintInfoGrid } from "@/components/shared/print";

interface TransactionPrintTemplateProps {
  receipt?: any;
  config?: any;
}

export default function TransactionPrintTemplate({
  receipt = {},
  config,
}: TransactionPrintTemplateProps) {
  const c = getCompanyConfig(config);
  const docId =
    receipt?.receiptId ||
    (receipt?.id?.length > 20 ? receipt.id.slice(0, 8).toUpperCase() : receipt?.id) ||
    "TC";
  const isIncome = receipt?.type === "INCOME";
  const formName = isIncome ? "PHIẾU THU" : "PHIẾU CHI";
  const formNameEn = isIncome ? "Receipt Voucher" : "Payment Voucher";
  const amount = Number(receipt?.amount || 0);

  // Determine counterparty (Customer for income, Vendor/Employee for expense)
  const counterpartyName =
    receipt.customer?.name ||
    receipt.customer?.shortName ||
    receipt.vendor?.name ||
    receipt.receiverName ||
    receipt.payerName ||
    "—";

  const counterpartyAddress =
    receipt.customer?.address ||
    receipt.vendor?.address ||
    receipt.address ||
    "—";

  const counterpartyTaxCode =
    receipt.customer?.taxCode ||
    receipt.vendor?.taxCode ||
    "—";

  const paymentMethodLabel =
    receipt.paymentMethod === "TIEN_MAT"
      ? "Tiền mặt (VND)"
      : receipt.paymentMethod === "CHUYEN_KHOAN"
      ? "Chuyển khoản ngân hàng"
      : receipt.paymentMethod === "CAN_TRU"
      ? "Cấn trừ công nợ"
      : receipt.paymentMethod || "Tiền mặt / Chuyển khoản";

  const creatorName =
    receipt.creator?.fullName ||
    receipt.user?.fullName ||
    "Kế toán thanh toán APV";

  return (
    <div
      className="p-[10mm] text-black leading-snug text-[12px] bg-white w-full mx-auto min-h-[297mm]"
      style={{ fontFamily: "'Times New Roman', Times, serif" }}
      id="print-area"
    >
      {/* ========== COMPANY HEADER ========== */}
      <ISOPrintHeader
        config={config}
      />

      {/* ========== TITLE (CENTERED, NO QR) ========== */}
      <PrintDocumentTitle
        title={formName}
        subtitle={isIncome ? "RECEIPT VOUCHER" : "PAYMENT VOUCHER"}
        docNo={docId}
        date={receipt.receiptDate || receipt.createdAt || new Date()}
        dateLabel="Ngày chứng từ"
      />

      {/* ========== DOCUMENT METADATA (BORDERLESS 2-COL TABS) ========== */}
      <div className="mb-5">
        <PrintInfoGrid
          labelWidth="w-[125px]"
          leftColumn={[
            {
              label: "Hình thức",
              value: paymentMethodLabel,
            },
            {
              label: "Tham chiếu",
              value: receipt.referenceNumber || "—",
            },
          ]}
          rightColumn={[
            {
              label: "Số phiếu",
              value: docId,
              isBold: true,
              highlight: true,
            },
            {
              label: "Ngày chứng từ",
              value: formatDate(receipt.receiptDate || receipt.createdAt || new Date()),
            },
          ]}
        />
      </div>

      {/* ========== MAIN VOUCHER DETAILS ========== */}
      <div className="text-[12px] space-y-3 mb-8 px-2 leading-relaxed border border-slate-800 p-4 rounded-sm bg-slate-50/30">
        <div className="flex items-start">
          <div className="font-semibold whitespace-nowrap min-w-[190px]">
            {isIncome ? "Họ tên người nộp tiền:" : "Họ tên người nhận tiền:"}
          </div>
          <div className="flex-1 border-b border-dotted border-slate-600 font-bold uppercase text-[13px] text-slate-900 pb-0.5">
            {counterpartyName}
          </div>
        </div>

        <div className="flex items-start">
          <div className="font-semibold whitespace-nowrap min-w-[190px]">
            Địa chỉ / Đơn vị:
          </div>
          <div className="flex-1 border-b border-dotted border-slate-600 text-slate-800 pb-0.5">
            {counterpartyAddress}
          </div>
        </div>

        {counterpartyTaxCode !== "—" && (
          <div className="flex items-start">
            <div className="font-semibold whitespace-nowrap min-w-[190px]">
              Mã số thuế:
            </div>
            <div className="flex-1 border-b border-dotted border-slate-600 font-mono text-slate-800 pb-0.5">
              {counterpartyTaxCode}
            </div>
          </div>
        )}

        <div className="flex items-start">
          <div className="font-semibold whitespace-nowrap min-w-[190px]">
            Lý do {isIncome ? "thu tiền" : "chi tiền"}:
          </div>
          <div className="flex-1 border-b border-dotted border-slate-600 text-slate-800 pb-0.5">
            {receipt.notes ||
              (isIncome
                ? "Thu tiền thanh toán đơn hàng bao bì"
                : "Chi tiền thanh toán vật tư / dịch vụ gia công")}
          </div>
        </div>

        <div className="flex items-start">
          <div className="font-semibold whitespace-nowrap min-w-[190px]">
            Số tiền ({isIncome ? "Đã thu" : "Đã chi"}):
          </div>
          <div className="flex-1 border-b border-dotted border-slate-600 font-bold text-[15px] text-red-700 tabular-nums pb-0.5">
            {formatMoney(amount)}
          </div>
        </div>

        <div className="flex items-start">
          <div className="font-semibold whitespace-nowrap min-w-[190px] italic">
            Số tiền viết bằng chữ:
          </div>
          <div className="flex-1 border-b border-dotted border-slate-600 italic font-semibold text-slate-900 pb-0.5">
            {amount > 0 ? numberToVietnameseWords(amount, "đồng") : "Không đồng"}
          </div>
        </div>

        <div className="flex items-start">
          <div className="font-semibold whitespace-nowrap min-w-[190px]">
            Chứng từ gốc kèm theo:
          </div>
          <div className="flex-1 border-b border-dotted border-slate-600 text-slate-700 pb-0.5">
            {receipt.originalDocsLabel ||
              (receipt.referenceNumber
              ? `01 Hóa đơn / Biên bản giao hàng (${receipt.referenceNumber})`
              : "01 Hóa đơn / Chứng từ thanh toán hợp lệ")}
          </div>
        </div>
      </div>

      {/* ========== ACCOUNTING & BANK FOOTNOTE ========== */}
      <div className="text-[10px] text-slate-600 italic mb-10 px-2">
        * Chứng từ kế toán lập theo quy định chế độ kế toán doanh nghiệp (Thông tư 200/2014/TT-BTC ). Đã kiểm tra số quỹ thực tế.
      </div>

      {/* ========== 4-PARTY SIGNATURES ========== */}
      <div className="grid grid-cols-4 gap-2 mt-8 text-[11px] text-center break-inside-avoid">
        <div>
          <div className="font-bold mb-1">GIÁM ĐỐC</div>
          <div className="text-slate-500 italic text-[10px] mb-16">
            (Ký & đóng dấu)
          </div>
          <div className="font-medium text-slate-800">....................</div>
        </div>
        <div>
          <div className="font-bold mb-1">KẾ TOÁN TRƯỞNG</div>
          <div className="text-slate-500 italic text-[10px] mb-16">
            (Ký & duyệt chi/thu)
          </div>
          <div className="font-medium text-slate-800">....................</div>
        </div>
        <div>
          <div className="font-bold mb-1">
            {isIncome ? "NGƯỜI NỘP TIỀN" : "NGƯỜI NHẬN TIỀN"}
          </div>
          <div className="text-slate-500 italic text-[10px] mb-16">
            (Ký & ghi rõ họ tên)
          </div>
          <div className="font-medium text-slate-800">
            {counterpartyName !== "—"
              ? counterpartyName.slice(0, 22)
              : "...................."}
          </div>
        </div>
        <div>
          <div className="font-bold mb-1">THỦ QUỸ</div>
          <div className="text-slate-500 italic text-[10px] mb-16">
            (Đã nhận / chi đủ tiền)
          </div>
          <div className="font-medium text-slate-800">{creatorName}</div>
        </div>
      </div>
    </div>
  );
}
